import { useMemo } from 'react';

import { type Coupon } from '@gymcloud/core';
import { Flex, Text } from 'web-styled';

import PaymentMethod from './PaymentMethod';
import CouponForm, { type CouponState } from 'web-components/CouponForm/CouponForm';
import * as S from './styled';

function applyDiscount(priceStr: string, percentOff: number): { total: number; discount: number } {
  const price = parseFloat(priceStr);
  const discount = +(price * (percentOff / 100)).toFixed(2);
  const total = +(price - discount).toFixed(2);
  return { total, discount };
}

interface Plan {
  id: number | string
  name: string
  price: string
  interval: 'year' | 'month'
}

interface Props {
  plan: Plan;
  coupon: Coupon | null;
  couponState: CouponState;
  onCheckCoupon: (coupon: string) => void;
}

const PriceSummary = ({ plan, discount, total }: { plan: Plan; discount: number; total: number }) => (
  <Flex column spacing={8} width="100%">
    <Text size="16px" weight="700" color="#121827">Billing Amount</Text>
    <Flex width="100%" align="center" justify="space-between">
      <Text size="16px" color="#121827">Subtotal:</Text>
      <Text size="20px" color="#121827" decoration={discount > 0 ? 'line-through' : 'inherit'}>
        ${parseFloat(plan.price).toFixed(2)}
      </Text>
    </Flex>
    <Flex width="100%" align="center" justify="space-between">
      {discount > 0 ? (
        <>
          <Text size="16px" color="#121827">Discount:</Text>
          <Text size="16px" color="#121827" weight="700">-{discount.toFixed(2)}</Text>
        </>
      ) : (
        <Text size="16px" color="#cccccc">Discount:</Text>
      )}
    </Flex>
    <Flex width="100%" align="center" justify="space-between">
      <Text size="16px" color="#121827" weight="700">Total:</Text>
      <Text size="20px" color="#121827" weight="700">${total.toFixed(2)}</Text>
    </Flex>
  </Flex>
)

const MobileCheckout = ({ plan, coupon, couponState, onCheckCoupon }: Props) => {
  const { total, discount } = useMemo(
    () => applyDiscount(plan.price, coupon?.percent_off || 0),
    [coupon, plan.price]
  )

  return (
    <Flex column spacing={15} width="100%">
      <S.Item>
        <Flex column>
          <Text color="#121827" size="16px" weight="700">Product</Text>
          <Text color="#000000" size="16px">{plan.name}</Text>
        </Flex>
        <Flex>
          <Text color="#000000" size="20px">${parseFloat(plan.price).toFixed(2)}</Text>
          <Text color="#000000" size="20px">/{plan.interval === 'year' ? 'yr' : 'mo'}</Text>
        </Flex>
      </S.Item>
      <Flex column spacing={15} width="100%">
        <PaymentMethod />
        <hr />
        <CouponForm buttonText="Apply" couponState={couponState} onSubmit={onCheckCoupon} />
        <hr />
        <PriceSummary plan={plan} discount={discount} total={total} />
      </Flex>
    </Flex>
  );
};

export default MobileCheckout;
